import { useNavigate, useParams, Link} from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { GlobalInfo } from "../../../../../Contexts/GlobalContext";
import { LabsService } from "../../../../../Services/Labs.service";
import TokenService from "../../../../../Services/Token.service";
import Toaster from "../../../../../Components/Toaster";
import { Formik, Form, Field, ErrorMessage  } from "formik";
import * as Yup from "yup";
import Loader from "../../../../../Components/Loader";
import { formatDate } from '../../../../../Utils/Common';



function LabHolidayEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { appUserIsLogin, theme, toggleSidebar, isSidebarVisible } = useContext(GlobalInfo);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('');
    const [loading, setLoading] = useState(false);
    const previousUrl = TokenService.getPrevLink();
    const [startDate, setStartDate] = useState(null);
    const [minStartDate, setMinStartDate] = useState(null);
    const [minEndDate, setMinEndDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [fieldValue, setFieldValue] = useState(null);
    const [holidayDetail, setHolidayDetail] = useState(null);
    const [initialValues, setInitialValues] = useState(null);
    const [labs, setLabs] = useState([]);

    // Validation Schema using Yup
    const validationSchema = Yup.object().shape({
        from_date: Yup.date().required('From date is required'),
        to_date: Yup.date().required('To date is required'),
        lab_id: Yup.string().required('Lab selection is required'),
        reason: Yup.string().required('Reason is required'),
    });

    useEffect(() => {
        setMinStartDate(new Date());
        setMinEndDate(new Date());
        fetchLabs();
        fetchHolidayDetail(id);
    }, [id]); 

    const fetchLabs = async () => {
        try {
            const response = await LabsService.getLabList();
            setLabs(response.labs || []);
        } catch (error) {
            console.error("Error fetching labs", error);
        }
    };

    const fetchHolidayDetail = async (id) => {
        setLoading(true);
        try {

            LabsService.fetchHolidayDetail(id)
            .then(response => {
                
                const holiday = response.holiday;
                setHolidayDetail(holiday);
                setStartDate(new Date(holiday.from_date));
                setEndDate(new Date(holiday.to_date));
                setMinStartDate(new Date(holiday.from_date));
                setMinEndDate(new Date(holiday.from_date));
                
                setInitialValues({
                    from_date: new Date(holiday.from_date),
                    to_date: new Date(holiday.to_date),
                    reason: holiday.reason || '',
                    lab_id: holiday.lab_id
                });          
            })
            .catch(error => {
                setToastMessage('Error fetching vacation details');
                setShowToast(true);
                setToastVariant('error');
                console.log("Error fetching vacation details:", error);
            });

        } catch (error) {
            setToastMessage(error.message || 'Something went wrong.');
            setShowToast(true);
            setToastVariant('error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        
        setLoading(true);
        const holidayData = {
            action: "update",
            lab_id: values.lab_id,
            from_date: formatDate(values.from_date), 
            to_date: formatDate(values.to_date),
            reason: values.reason,
            holiday_id: id,
        };

        try {
            const data = await LabsService.updateHoliday(holidayData);
            if (!data.status) {
                setToastMessage(data.message);
                setToastVariant('error');
            } else {
                setToastMessage(data.message);
                setToastVariant('success');

                setTimeout(() => {
                    // navigate(`${previousUrl}/lab-hours`);
                    navigate(`${previousUrl}`);
                }, 1000);
            }
        } catch (error) {
            setToastMessage('Failed to update vacation.');
            setToastVariant('error');
        } finally {
            setLoading(false);
            setShowToast(true);
        }
    };

    return (
        <>
            <Loader loading={loading} />
            <div className={`${!isSidebarVisible ? 'collapsed-body' : ''}`}>
                <div className="el-middle-wrapper">
                    <h2 className="el-title">
                        Edit Vacation                       
                    </h2>
                    <Link className="back-link" to={`${previousUrl}`}><i className='el-icon-back'></i> Lab Hours</Link>

                    <div className="el-bg-table el-super-admin ps-45 pt-1">
                        <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                                enableReinitialize={true}
                            >
                                {({ values, setFieldValue }) => {

                                return (
                                <Form> 

                                    <div className="row mt-3 col-xl-6"> 
                                        <div className="col-md-12">
                                            <label htmlFor="lab_id" className="form-label">Lab<span className="text-red">*</span></label> 
                                            <Field as="select" name="lab_id" className="form-control">
                                                <option value="">Select a Lab</option>
                                                {labs.map((lab) => (
                                                    <option key={lab.id}  value={String(lab.id)}>{lab.name}</option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="lab_id" component="div" className="text-danger" />
                                        </div>
                                    </div>

                                    <div className="row mt-3 col-xl-6">                                        
                                        <div className="col-md-6" key="from_date">
                                            <label htmlFor="from_date" className="form-label">From Date<span className="text-red">*</span></label>

                                            <DatePicker            
                                                selected={startDate}
                                                onChange={(date) => {
                                                    setStartDate(date);
                                                    setEndDate(null);
                                                    setMinEndDate(date);
                                                    setFieldValue('from_date', date);
                                                }}
                                                selectsStart  
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={minStartDate}
                                                dateFormat="MMMM d, yyyy"
                                                placeholderText="Select From Date"
                                                name="from_date"
                                                id="from_date"
                                                className="form-control"
                                                value={values?.from_date}
                                            />
                                            <ErrorMessage name="from_date" component="div" className="text-danger" />
                                        </div>   

                                        <div className="col-md-6" key="to_date">
                                            <label htmlFor="to_date" className="form-label">To Date<span className="text-red">*</span></label>

                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => {
                                                    setEndDate(date);
                                                    setFieldValue('to_date', date);
                                                }}                                                                                             
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={minEndDate} 
                                                dateFormat="MMMM d, yyyy"
                                                placeholderText="Select To Date"
                                                name="to_date"
                                                id="to_date"
                                                className="form-control"
                                                value={values?.to_date}
                                            />
                                            <ErrorMessage name="to_date" component="div" className="text-danger" />
                                        </div>   
                                    </div> 
                                    <div className="row mt-3 mb-3 col-xl-6">
                                        <div className=" " key="reason">
                                            <label htmlFor="reason" className="form-label">Reason <span className="text-red">*</span></label>
                                            <textarea
                                                id="reason"
                                                name="reason"
                                                className="form-control"
                                                rows="5"
                                                cols="50"
                                                value={values?.reason}
                                                onChange={(e) => setFieldValue('reason', e.target.value)}
                                                placeholder="Enter a Reason"
                                            />
                                            <ErrorMessage name="reason" component="div" className="text-danger" />
                                        </div>  
                                    </div> 
                                    <div className="d-flex align-items-center">
                                    <button type="submit" className="el-main-button"> <span> Submit</span></button>
                                    <Link className="back-link ms-3" to={`${previousUrl}`}>Close</Link>
                                    </div>
                                    
                                </Form>
                                );
                            }}
                        </Formik> 
                    </div>
                    <Toaster
                        show={showToast}
                        message={toastMessage}
                        variant={toastVariant}
                        onClose={() => setShowToast(false)}
                    />
                </div>
            </div>
        </>
    );
}

export default LabHolidayEdit;